
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState } from "react"
import { UseFormReturn } from "react-hook-form"

type FormData = {
  first_name: string
  last_name: string
  state: string
  city: string
  role: string
  new_password?: string
  prefeito?: string
}

type UserNameFieldsProps = {
  form: UseFormReturn<FormData>
}

export function UserNameFields({ form }: UserNameFieldsProps) {
  return (
    <>
      <FormField
        control={form.control}
        name="first_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nome</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="last_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Sobrenome</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}

type UserLocationFieldsProps = {
  form: UseFormReturn<FormData>
  states: any[]
  cities: any[]
  selectedState: string
  onStateChange: (value: string) => void
}

export function UserLocationFields({ 
  form, 
  states, 
  cities, 
  selectedState, 
  onStateChange 
}: UserLocationFieldsProps) {
  return (
    <>
      <FormField
        control={form.control}
        name="state"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Estado</FormLabel>
            <Select
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                onStateChange(value)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um estado" />
              </SelectTrigger>
              <SelectContent>
                {states?.map((state: any) => (
                  <SelectItem key={state.id} value={state.sigla}>
                    {state.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="city"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Cidade</FormLabel>
            <Select
              value={field.value}
              onValueChange={field.onChange}
              disabled={!selectedState}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma cidade" />
              </SelectTrigger>
              <SelectContent>
                {cities?.map((city: any) => (
                  <SelectItem key={city.id} value={city.nome}>
                    {city.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}

type UserRoleFieldsProps = {
  form: UseFormReturn<FormData>
  mayors: any[]
}

export function UserRoleFields({ form, mayors }: UserRoleFieldsProps) {
  return (
    <>
      <FormField
        control={form.control}
        name="role"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Função</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="vereador">Vereador</SelectItem>
                <SelectItem value="vereador-presidente da câmara">Vereador - Presidente da Câmara</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="prefeito"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Prefeito</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o prefeito" />
              </SelectTrigger>
              <SelectContent>
                {mayors?.map((mayor) => (
                  <SelectItem key={mayor.mayor_name} value={mayor.mayor_name}>
                    {mayor.mayor_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}

type UserPasswordFieldProps = {
  form: UseFormReturn<FormData>
}

export function UserPasswordField({ form }: UserPasswordFieldProps) {
  const [showPassword, setShowPassword] = useState(false)
  
  return (
    <FormField
      control={form.control}
      name="new_password"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Nova Senha</FormLabel>
          <div className="relative">
            <FormControl>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Digite a nova senha"
                {...field}
              />
            </FormControl>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
